import React, { useState, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Project } from '@/types/project';
import { ProjectPermission } from '@/types/auth';
import ProjectContent from './ProjectContent';
import { usePredictiveTabs } from '@/hooks/usePredictiveTabs';

interface OptimizedProjectContentProps {
  project: Project;
  permissions: ProjectPermission;
}

// Debounce window for the hover/focus prefetch — avoids firing on every
// mouse-move event as the user scans the page.
const PREFETCH_DEBOUNCE_MS = 300;
// If the project query has been refreshed within this window, skip prefetch.
const PREFETCH_FRESHNESS_MS = 30_000;

const OptimizedProjectContent = React.memo(({ project, permissions }: OptimizedProjectContentProps) => {
  const queryClient = useQueryClient();
  const [currentTab] = useState('overview');

  // Week 3 Optimization: Predictive tab preloading
  const { prefetchLikelyTabs, isEnabled: isPredictiveEnabled } = usePredictiveTabs({
    projectId: project.id,
    currentTab,
  });

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ✅ Phase A perf fix: debounce hover/focus prefetch + skip when project cache is fresh
  const handlePrefetchAll = useCallback(() => {
    if (!isPredictiveEnabled) return;

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;

      // Skip prefetch entirely if the project query was updated recently.
      // Matches any cached query whose key includes the project id.
      const projectQueries = queryClient.getQueriesData({
        predicate: (query) => query.queryKey.some((k) => k === project.id),
      });
      const now = Date.now();
      const hasFreshProjectQuery = projectQueries.some(([key]) => {
        const state = queryClient.getQueryState(key as readonly unknown[]);
        return state?.dataUpdatedAt && now - state.dataUpdatedAt < PREFETCH_FRESHNESS_MS;
      });

      if (hasFreshProjectQuery) return;

      prefetchLikelyTabs();
    }, PREFETCH_DEBOUNCE_MS);
  }, [isPredictiveEnabled, prefetchLikelyTabs, queryClient, project.id]);

  return (
    <div
      onMouseEnter={handlePrefetchAll}
      onFocus={handlePrefetchAll}
      className="w-full"
    >
      <ProjectContent project={project} permissions={permissions} />
    </div>
  );
});

OptimizedProjectContent.displayName = 'OptimizedProjectContent';

export default OptimizedProjectContent;
